import { motion } from "framer-motion";
import { Star, Quote, ArrowUpRight } from "lucide-react";
import { useLang, useT } from "../i18n";
import { REVIEWS } from "../data/content";

const Stars = () => (
  <div className="flex gap-1" aria-label="4.5 out of 5 stars">
    {[0, 1, 2, 3].map((i) => (
      <Star key={i} className="w-5 h-5 fill-saffron-500 text-saffron-500" />
    ))}
    <span className="relative w-5 h-5">
      <Star className="w-5 h-5 text-saffron-500 absolute inset-0" />
      <span className="absolute inset-0 overflow-hidden w-1/2">
        <Star className="w-5 h-5 fill-saffron-500 text-saffron-500" />
      </span>
    </span>
  </div>
);

export const Reviews = () => {
  const t = useT();
  const lang = useLang();

  return (
    <section id="reviews" className="bg-bergamote-900 text-cream-50 py-24 sm:py-36 relative overflow-hidden" data-testid="reviews-section">
      <span aria-hidden className="font-serif font-bold absolute -top-10 right-0 text-[18vw] leading-none select-none pointer-events-none" style={{ WebkitTextStroke: "1px rgba(163,201,153,0.18)", color: "transparent" }}>
        4.5
      </span>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-bergamote-300 mb-5">{t.ch4_label}</p>
        <h2 className="font-serif font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl text-cream-50" data-testid="reviews-title">
          {t.ch4_title}
        </h2>
        <a
          href="https://www.google.com/maps/search/?api=1&query=La+Bergamote+Gen%C3%A8ve+-+Salon+de+th%C3%A9"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="reviews-google-link"
          className="group mt-4 inline-flex items-center gap-2 text-saffron-400 hover:text-saffron-500 transition-colors duration-300 text-sm font-semibold"
        >
          <span className="relative after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-0 after:bg-saffron-400 group-hover:after:w-full after:transition-[width] after:duration-300">
            {t.reviews_all_google}
          </span>
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
        </a>

        <div className="mt-12 grid lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-4"
          >
            <p className="font-serif text-8xl font-semibold text-cream-50" data-testid="reviews-score">4.5</p>
            <div className="mt-3"><Stars /></div>
            <p className="mt-3 text-bergamote-200 text-sm" data-testid="reviews-count">{t.ch4_score_label}</p>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="reviews-keywords">
              {t.ch4_keywords.map((k) => (
                <span key={k} className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border border-bergamote-300/30 text-bergamote-200">
                  {k}
                </span>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
            {REVIEWS.map((r, i) => (
              <motion.article
                key={r.author}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12 }}
                className={`bg-bergamote-800/60 border border-bergamote-300/15 rounded-2xl p-7 backdrop-blur-sm hover:border-saffron-500/40 hover:-translate-y-1 transition-[border-color,transform] duration-300 ${i === 2 ? "sm:col-span-2" : ""}`}
                data-testid={`review-card-${i}`}
              >
                <Quote className="w-5 h-5 text-saffron-500 mb-4" />
                <p className="text-bergamote-100 leading-relaxed text-sm sm:text-base">{r.text[lang]}</p>
                <footer className="mt-5 flex items-center justify-between">
                  <div>
                    <p className="font-serif text-lg font-semibold text-cream-50">{r.author}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-bergamote-300">{r.meta[lang]}</p>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="w-3.5 h-3.5 fill-saffron-500 text-saffron-500" />
                    ))}
                  </div>
                </footer>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
